import React, { SetStateAction } from 'react';
import { getContentSchemaFromContentFul } from '../../redux/actions/common/common-action';
import {
  CUSTOM_ALERT_BANK_VERIFICATION_ERROR,
  CUSTOM_ALERT_DEFAULT_VALUES,
  CUSTOM_ALERT_INVALID_BANK_NAME,
  CUSTOM_ALERT_INVALID_BANK_NAME_MINOR_ERROR
} from './constants';
import { CustomInfoAlertMessageStateType } from './types';
import { compileTemplateNew } from '../../utils/library';
import { IN } from '../../utils/constants';

export const getContentForAlertPopUp = async (
  contentId: string,
  contentType: string,
  setState: React.Dispatch<SetStateAction<CustomInfoAlertMessageStateType>>,
  setIsLoading: React.Dispatch<SetStateAction<boolean>>,
  bankNameInvalid: boolean,
  displayBeneName: boolean | undefined
) => {
  const DEFAULT_VALUES = bankNameInvalid
    ? displayBeneName
      ? CUSTOM_ALERT_INVALID_BANK_NAME_MINOR_ERROR
      : CUSTOM_ALERT_INVALID_BANK_NAME
    : CUSTOM_ALERT_DEFAULT_VALUES;
  const response = await getContentSchemaFromContentFul(contentType, contentId);
  const heading = response?.headingLabel?.value || DEFAULT_VALUES.heading;
  const desc = response?.messageLabel?.value || DEFAULT_VALUES.desc;
  const primaryCtaText = response?.urlLabel?.value || DEFAULT_VALUES.primaryCtaText;
  const secondaryCtaText =
    response?.styleJson?.value?.secondaryCTA || DEFAULT_VALUES.secondaryCtaText;
  const icon = response?.cardImg?.value[0]?.target?.fields?.url
    ? `https:${response.cardImg.value[0].target.fields.url}`
    : DEFAULT_VALUES.icon;

  const shareMessage = response?.labels?.value?.shareMessage || DEFAULT_VALUES.shareMessage;
  const transactionFlowLabel =
    response?.labels?.value?.transactionFlow || DEFAULT_VALUES.transactionFlow;

  const alertPopupContent = {
    heading,
    desc,
    icon,
    primaryCtaText,
    secondaryCtaText,
    shareMessage,
    transactionFlowLabel
  };

  setState(alertPopupContent);
  setIsLoading(false);
};

export const transformBankDetails = (
  data: { [key: string]: any },
  values: { [key: string]: string },
  messageObject: {
    part1: string;
    part2: string;
    part3: string;
    part4: string;
  },
  showBankNameinEmail: string,
  isEmailMessage = false
) => {
  const results: { [key: string]: string } = {};
  const keys: { name: string; label: string }[] = [];
  for (const [key, value] of Object.entries(data || {})) {
    if (value?.render) {
      // ignore internal fields for UPI payout method
      if (
        values?.bank_account_country === IN &&
        values?.proxy_type === 'VPA' &&
        ['proxy_type', 'beneficiary_bank_name'].includes(key)
      ) {
        continue;
      }
      keys.push({ name: value?.render?.value_field_name, label: value?.render?.label });
    }
  }

  if (keys.length > 0) {
    keys.forEach((key) => {
      let val = values[key.name];
      if (key.name === 'beneficiary_bank_name') {
        val = extractBankName(val);
      }

      results[key.label] = val;
    });
  }

  const details = showBankNameinEmail ? showBankNameinEmail : buildDetailsString(results);
  const message = formatFinalMessage(values, details, messageObject, isEmailMessage);
  return message;
};

const formatFinalMessage = (
  values: { [key: string]: string },
  details: string,
  messageObject: {
    part1: string;
    part2: string;
    part3: string;
    part4: string;
  },
  isEmailMessage = false
) => {
  const { part1, part2, part3, part4 } = messageObject;
  const compiledPart1 = compileTemplateNew(part1, {
    name: `${values?.first_name || values?.beneficiary_name || ''} ${values?.middle_name || ''} ${
      values?.last_name || ''
    } ${values?.company_name || ''}`.trim()
  });

  let message = `${compiledPart1}<br/><br/>${part2}<br/><br/>${details}<br/>${part3}<br/><br/>${part4}`;
  if (isEmailMessage) {
    // because most of the mail clients use CRLF line breaks, we need to use the following character sequence
    message = message.replaceAll('<br/>', '%0D%0A');
  }
  return message;
};

// extracts actual bank name from the value for bank name field, which can contain routing code inside parenthesis
const extractBankName = (bankName: string) => {
  const startIndex = bankName.indexOf('(');
  const endIndex = bankName.indexOf(')');
  if (startIndex !== -1 && endIndex !== -1) {
    const charsToBeOmitted = bankName.substring(startIndex, endIndex + 1);
    bankName = bankName.replace(charsToBeOmitted, '');
  }
  return bankName;
};

// builds a string that contains bank details from processed results
const buildDetailsString = (bankDetailsEntries: { [key: string]: string }) => {
  let details = '';
  for (const [key, value] of Object.entries(bankDetailsEntries)) {
    details += `${key}: ${value}<br/>`;
  }
  return details;
};

export const getCustomAlertErrorDefaultValues = (
  bankNameInvalid: boolean,
  displayBeneName: boolean | undefined,
  isBankVerificationUnavailable: boolean
) => {
  if (bankNameInvalid) {
    if (displayBeneName) {
      return CUSTOM_ALERT_INVALID_BANK_NAME_MINOR_ERROR;
    } else {
      return CUSTOM_ALERT_INVALID_BANK_NAME;
    }
  } else if (isBankVerificationUnavailable) {
    return CUSTOM_ALERT_BANK_VERIFICATION_ERROR;
  } else {
    return CUSTOM_ALERT_DEFAULT_VALUES;
  }
};
